import React, { useState, useEffect } from 'react';
import { Header, Button } from '../layout';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { setDocumentTitle } from '../utils/titleUtils';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const BASE_URL = API_URL.replace('/api', '');

const DocumentsPage = () => {
    const { user } = useAuth();
    const [generalDocuments, setGeneralDocuments] = useState([]);
    const [privateDocuments, setPrivateDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('general');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setDocumentTitle('Consultazione Documenti');
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setIsLoading(true);
        try {
            // Determine user type and id
            let userType = null;
            let userId = null;

            if (user?.userType === 'client') {
                userType = 'client';
                userId = user?.id || user?.client_data?.CustSupp;
            } else if (user?.userType === 'agent') {
                userType = 'agent';
                userId = user?.id || user?.agent_data?.Salesperson;
            }

            const response = await api.getUserDocuments(userType, userId);

            if (response.status === 'success') {
                setGeneralDocuments(response.general_documents || []);
                setPrivateDocuments(response.private_documents || []);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Impossibile caricare i documenti'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = (document) => {
        const fileUrl = `${BASE_URL}${document.file_path}`;
        window.open(fileUrl, '_blank');
    };

    const handlePreview = (document) => {
        const fileUrl = `${BASE_URL}${document.file_path}`;
        const isPdf = document.mime_type === 'application/pdf' || document.original_filename?.endsWith('.pdf');
        const isImage = document.mime_type?.startsWith('image/');

        if (isPdf || isImage) {
            Swal.fire({
                title: document.title,
                html: isPdf
                    ? `<iframe src="${fileUrl}" style="width:100%; height:70vh; border:none;"></iframe>`
                    : `<img src="${fileUrl}" style="max-width:100%; max-height:70vh;" alt="${document.title}" />`,
                width: '90%',
                showCloseButton: true,
                showConfirmButton: false,
            });
        } else {
            handleDownload(document);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getFileIcon = (mimeType, filename) => {
        const isPdf = mimeType === 'application/pdf' || filename?.endsWith('.pdf');
        const isImage = mimeType?.startsWith('image/');

        if (isPdf) {
            return (
                <svg className="w-10 h-10 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M13,9V3.5L18.5,9H13M10.5,11C10.78,11 11,11.22 11,11.5V12.5C11,12.78 10.78,13 10.5,13H9V15H8V11H10.5M12,11H14C14.55,11 15,11.45 15,12V14C15,14.55 14.55,15 14,15H12V11M13,12V14H14V12H13M15.5,11H18V12H16.5V12.5H17.5V13.5H16.5V15H15.5V11Z" />
                </svg>
            );
        }

        if (isImage) {
            return (
                <svg className="w-10 h-10 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z" />
                </svg>
            );
        }

        return (
            <svg className="w-10 h-10 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13,9V3.5L18.5,9M6,2C4.89,2 4,2.89 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6Z" />
            </svg>
        );
    };

    const filteredGeneralDocs = generalDocuments.filter(doc =>
        doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.original_filename?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredPrivateDocs = privateDocuments.filter(doc =>
        doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.original_filename?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const DocumentCard = ({ document }) => (
        <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-4 border border-gray-100">
            <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                    {getFileIcon(document.mime_type, document.original_filename)}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {document.title}
                    </h3>
                    {document.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {document.description}
                        </p>
                    )}
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                        <span>{document.original_filename}</span>
                        <span>{formatFileSize(document.file_size)}</span>
                        <span>{formatDate(document.created_at)}</span>
                    </div>
                </div>
            </div>
            <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-100">
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handlePreview(document)}
                    className="flex-1"
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Visualizza
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleDownload(document)}
                    className="flex-1"
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Scarica
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100">
            <Header
                title="Consultazione Documenti"
                backText="Dashboard"
                backTo="/dashboard"
            />

            <div className="container mx-auto px-4 py-6">
                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Cerca documenti..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-3 pl-12 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        <svg
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('general')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'general'
                                    ? 'border-red-500 text-red-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                Documenti Generali
                                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                    {filteredGeneralDocs.length}
                                </span>
                            </div>
                        </button>
                        {privateDocuments.length > 0 && (
                            <button
                                onClick={() => setActiveTab('private')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'private'
                                        ? 'border-red-500 text-red-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Documenti Riservati
                                    <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                                        {filteredPrivateDocs.length}
                                    </span>
                                </div>
                            </button>
                        )}
                    </nav>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                    </div>
                ) : (
                    <div>
                        {activeTab === 'general' && (
                            <div>
                                {filteredGeneralDocs.length === 0 ? (
                                    <div className="text-center py-12 bg-white rounded-lg shadow">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun documento</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {searchTerm ? 'Nessun risultato per la ricerca' : 'Non ci sono documenti generali disponibili'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filteredGeneralDocs.map((doc) => (
                                            <DocumentCard key={doc.id} document={doc} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'private' && (
                            <div>
                                {filteredPrivateDocs.length === 0 ? (
                                    <div className="text-center py-12 bg-white rounded-lg shadow">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun documento riservato</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {searchTerm ? 'Nessun risultato per la ricerca' : 'Non ci sono documenti riservati per il tuo account'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filteredPrivateDocs.map((doc) => (
                                            <DocumentCard key={doc.id} document={doc} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentsPage;
